import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";
import {
  Heart,
  Users,
  BookOpen,
  Calendar,
  MessageCircle,
  Video,
  Clock,
  CalendarDays,
  Phone,
  Mail,
  Award,
  Handshake,
  Globe2,
  Target,
  Sparkles,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { supabase } from "../../../src/integrations/supabase/client";
interface Activity {
  id: string;
  title: string;
  zoom_link?: string;
  meeting_id?: string;
  passcode?: string;
  schedule?: string;
  members?: { full_name: string };
}

const WomensMinistry = () => {
  const [womensActivities, setWomensActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWomensActivities();
  }, []);

  const fetchWomensActivities = async () => {
    try {
      const { data, error } = await supabase
        .from("activities")
        .select(
          `
          *,
          facilitator:facilitator_id (
            full_name,
            email,
            phone
          )
        `,
        )
        .eq("activity_type", "womens_ministry")
        .eq("is_active", true);

      if (error) throw error;
      setWomensActivities(data || []);
    } catch (error) {
      console.error("Error fetching women's activities:", error);
    } finally {
      setLoading(false);
    }
  };
  const ministryAreas = [
    {
      icon: Heart,
      title: "Mentorship Programs",
      description:
        "Connect with experienced Christian women for guidance and support in your academic and spiritual journey",
    },
    {
      icon: BookOpen,
      title: "Bible Studies",
      description: "Join women-only Bible study groups focused on topics relevant to young women in academia",
    },
    {
      icon: Users,
      title: "Fellowship Groups",
      description: "Build meaningful friendships with other Christian women navigating university life",
    },
    {
      icon: Calendar,
      title: "Special Events",
      description: "Participate in women's conferences, retreats, and special gatherings throughout the year",
    },
  ];

  // Default facilitators - can be overridden by database data
  const defaultFacilitators = [
    { name: "Angelin Bina Nokku", role: "Ministry Facilitator" },
    { name: "Leena Saladi", role: "Prayer Facilitator" },
  ];

  // Parse facilitators from JSONB field
  const facilitators =
    womensActivities.length > 0 && womensActivities[0]?.facilitators
      ? Array.isArray(womensActivities[0].facilitators)
        ? womensActivities[0].facilitators
        : []
      : defaultFacilitators;

  const objectives = [
    {
      title: "Fellowship",
      icon: Users as any,
      description: "Equip women to grow in relationship with Christ through prayer, Bible study and fellowship.",
    },
    {
      title: "Leadership",
      icon: Award as any,
      description:
        "Empower women to use their spiritual gifts to serve and reach others for Christ, realizing their identity in Christ and God-given potential to provide leadership at different levels.",
    },
    {
      title: "Partnership",
      icon: Handshake as any,
      description:
        "Engage in the vision of UESI/OFUESI and extend support through praying, supporting, mentoring, and conducting Bible studies.",
    },
    {
      title: "Companionship",
      icon: Heart as any,
      description:
        "Encourage women to fulfill responsibilities in their families and, in companionship with their spouses, involve in the extension of His Kingdom.",
    },
  ];

  const convertTimeToTimezones = (time: string, sourceTimezone: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    
    const timezoneOffsets = {
      'EST': 0,
      'CST': -1,
      'PST': -3,
    };
    
    const sourceOffset = timezoneOffsets[sourceTimezone as keyof typeof timezoneOffsets] || 0;
    
    const conversions = {
      EST: hours - sourceOffset + timezoneOffsets.EST,
      CST: hours - sourceOffset + timezoneOffsets.CST,
      PST: hours - sourceOffset + timezoneOffsets.PST,
    };
    
    const formatTime = (hour: number) => {
      const adjustedHour = ((hour + 24) % 24);
      const period = adjustedHour >= 12 ? 'PM' : 'AM';
      const displayHour = adjustedHour === 0 ? 12 : adjustedHour > 12 ? adjustedHour - 12 : adjustedHour;
      return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
    };
    
    return {
      EST: formatTime(conversions.EST),
      CST: formatTime(conversions.CST),
      PST: formatTime(conversions.PST),
    };
  };

  const formatMeetingTimes = (startTime: string, endTime: string, timezone: string) => {
    if (!startTime || !timezone) return null;
    
    const startTimes = convertTimeToTimezones(startTime, timezone);
    const endTimes = endTime ? convertTimeToTimezones(endTime, timezone) : null;
    
    return (
      <div className="mt-2 space-y-1">
        <p className="text-sm font-medium">Meeting Times:</p>
        <div className="text-sm text-muted-foreground space-y-0.5">
          <div><span className="font-semibold">EST:</span> {startTimes.EST}{endTimes ? ` - ${endTimes.EST}` : ''}</div>
          <div><span className="font-semibold">CST:</span> {startTimes.CST}{endTimes ? ` - ${endTimes.CST}` : ''}</div>
          <div><span className="font-semibold">PST:</span> {startTimes.PST}{endTimes ? ` - ${endTimes.PST}` : ''}</div>
        </div>
      </div>
    );
  };

  const activities = [
    { title: "Monthly Bible Study", icon: BookOpen as any, note: "Once a month" },
    { title: "Personal Sharing", icon: MessageCircle as any, note: "Encourage and learn" },
    { title: "Group Prayer", icon: Heart as any, note: "Ministry & personal needs" },
    { title: "One-on-one Fellowship", icon: Phone as any, note: "Connect over the phone" },
    { title: "Local Fellowship Cells", icon: Users as any, note: "Start OFUESI women's cells" },
    { title: "Regional WhatsApp Meets", icon: MessageCircle as any, note: "Stay connected regionally" },
    { title: "Pray for Intl. Students", icon: Globe2 as any, note: "Gather info & pray" },
    { title: "Trends & Needs", icon: Target as any, note: "Guide and support" },
    { title: "Family & Gifts", icon: Sparkles as any, note: "Balance & discover gifts" },
  ];

  useEffect(() => {
    document.title = "Women's Ministry – OFUESI";
    const desc = "OFUESI Women's Ministry: mentorship, Bible studies, events, and Zoom fellowship.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = window.location.href;
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto text-white space-y-6">
            <h1 className="text-4xl lg:text-6xl font-bold">Women's Ministry – Empowering Women in Faith</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              A supportive community for Christian women in academia, fostering spiritual growth, leadership
              development, and meaningful relationships.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
              >
                <Link to="/contact">Join Our Community</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Facilitators & Objectives */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Facilitators */}
            <Card className="h-full elegant-shadow">
              <CardHeader>
                <CardTitle>Facilitators 2025-2026</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-6">
                  {facilitators.map((f) => (
                    <div key={f.name} className="flex flex-col items-center text-center gap-2">
                      <Avatar className="h-20 w-20">
                        <AvatarImage src="" alt={`${f.name} placeholder`} />
                        <AvatarFallback className="text-lg">
                          {f.name
                            .split(" ")
                            .map((n) => n[0])
                            .slice(0, 2)
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="font-semibold text-foreground">{f.name}</div>
                      <div className="text-sm text-muted-foreground">{f.role}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Objectives */}
            <Card className="h-full elegant-shadow">
              <CardHeader>
                <CardTitle>Objectives</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {objectives.map((o, idx) => (
                    <div key={idx} className="rounded-lg border p-4 smooth-transition hover:shadow-md">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-primary p-2 rounded-full flex items-center justify-center">
                          <o.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{o.title}</div>
                          <p className="text-sm text-muted-foreground">{o.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Activities & Meeting Times */}
      <section className="py-10 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Activities */}
            <Card className="elegant-shadow">
              <CardHeader>
                <CardTitle>Activities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {activities.map((a, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border p-4 flex items-start gap-3 smooth-transition hover:shadow-md"
                    >
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <a.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">{a.title}</div>
                        <p className="text-sm text-muted-foreground">{a.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Zoom Meeting Times - Dynamic from Database */}
            <Card className="elegant-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-primary" />
                  Zoom Meeting Times
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <div>Loading activities...</div>
                ) : womensActivities.length === 0 ? (
                  <p className="text-muted-foreground">No scheduled meetings at this time.</p>
                ) : (
            womensActivities.map((activity) => (
              <div key={activity.id} className="rounded-xl border bg-white p-4 sm:p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{activity.title}</h3>
                    {activity.schedule && (
                      <p className="text-muted-foreground">{activity.schedule}</p>
                    )}
                    {activity.start_time && activity.timezone && 
                      formatMeetingTimes(activity.start_time, activity.end_time, activity.timezone)
                    }
                  </div>
                </div>
                {activity.zoom_link && (
                  <div className="mt-4">
                    <Button
                      asChild
                      variant="prayer"
                      className="justify-center bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
                    >
                      <a
                        href={activity.zoom_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Join Zoom for ${activity.title}`}
                      >
                        Join Zoom
                      </a>
                    </Button>
                    {activity.meeting_id && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Meeting ID: {activity.meeting_id}
                        {activity.passcode && ` • Passcode: ${activity.passcode}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))
                )}
                
                <p className="text-sm text-muted-foreground">
                  "To provide instruction and teaching regarding the Christian woman's responsibility according to Titus
                  2:3-5."
                </p>
                <div className="text-center mt-6">
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
                  >
                    <Link to="/contact">Contact Team</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ministry Areas */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Ministry Focus</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We provide holistic support for women pursuing higher education and professional careers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ministryAreas.map((area, index) => (
              <Card key={index} className="text-center elegant-shadow hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <area.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-lg">{area.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-16 bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto text-white space-y-6">
            <h2 className="text-3xl lg:text-4xl font-bold">Ready to Get Involved?</h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              Whether you're looking for community, mentorship, or opportunities to serve, there's a place for you in
              our women's ministry. Join us today!
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              <Card className="bg-white/10 border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">For Students</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-blue-100">
                    Join Bible studies, find mentors, and connect with other Christian women on your campus.
                  </p>
                  <Button
                    variant="outline"
                    className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
                  >
                    Connect with Local Group
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">For Professionals</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-blue-100">
                    Mentor younger women, lead Bible studies, and use your gifts to serve in ministry.
                  </p>
                  <Button
                    variant="outline"
                    className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
                  >
                    Volunteer Opportunities
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WomensMinistry;
